import React, { useCallback, useMemo, useState } from "react";

import { Box, Typography } from "@mui/material";
import { VisibilityOutlined } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import ChatPromptInput from "src/reusable/Chat/PromptInput/ChatPromptInput";
import { NucToast } from "src/reusable/components/Toast";
import { FileUploadState } from "src/reusable/types/fileUploadWithProgress";
import { useGetUserDetails } from "src/helpers/UserDetails";
import { useOnboardingStore } from "../stores/OnboardingStore";
import { shouldStageMessageForTurnHandoff } from "./atomAssistQueueUtils";
import { useChatStore } from "../stores/ChatStore";
import { useGetDialogueById } from "../AssistantServices";
import { ChatMessageWithParts, ContentType, ImageContent } from "../types";
import { type TokenUsageData } from "src/reusable/Chat/TokenUsageGauge";
import {
  type QueuedMessage,
  type SendMessageOptions,
} from "src/reusable/Chat/ChatStreaming/stores/CoreStreamingSlice";
import { newSendId, trackEvent } from "src/config/posthog";

type AtomAssistChatInputProps = {
  dialogue?: ChatMessageWithParts[];
  /**
   * When the page is embedded inside the projects view there is no
   * :dialogueId URL segment to read; the parent threads the id in via this
   * prop. Standalone callers omit it and the hook falls back to useParams.
   */
  dialogueId?: number;
  isChatConnected: boolean;
  isChatLoading: boolean;
  isChatEnded: boolean;
  /** True while the model is actively generating in the current turn. */
  isTurnActive?: boolean;
  InputButtons?: React.FC<any>;
  InputBar?: React.FC<any>;
  isSplashScreen?: boolean;
  terminateConnection: () => void;
  sendUserMessage: (
    text: string,
    images?: ImageContent[],
    opts?: SendMessageOptions,
  ) => Promise<boolean>;
  setDialogue?: (
    updater:
      | ChatMessageWithParts[]
      | ((prev: ChatMessageWithParts[]) => ChatMessageWithParts[]),
  ) => void;
  tokenUsage?: TokenUsageData | null;
  /** Messages queued locally, waiting to be sent when turn ends */
  pendingMessageQueue?: QueuedMessage[];
  /** Queue a message to be sent when the current turn ends */
  queueMessage?: (
    text: string,
    images?: ImageContent[],
    opts?: Pick<SendMessageOptions, "sendId">,
  ) => void;
  /** Remove a queued message by index */
  removeQueuedMessage?: (index: number) => void;
  /**
   * Project-level placeholder override (spec §5.10 "Message Acme
   * Project…"). Standalone callers omit it and keep the default copy.
   */
  composerPlaceholder?: string;
};

const AtomAssistChatInput = ({
  dialogue,
  dialogueId: dialogueIdProp,
  isChatConnected: _isChatConnected,
  isChatLoading,
  isChatEnded: _isChatEnded,
  isTurnActive = false,
  InputButtons,
  InputBar,
  isSplashScreen = false,
  terminateConnection,
  sendUserMessage,
  setDialogue: _setDialogue,
  tokenUsage,
  pendingMessageQueue = [],
  queueMessage,
  removeQueuedMessage,
  composerPlaceholder,
}: AtomAssistChatInputProps) => {
  const [userInput, setUserInput] = useState<string>("");
  const { dialogueId: dialogueIdParam } = useParams<{ dialogueId: string }>();
  const dialogueId =
    dialogueIdProp !== undefined
      ? dialogueIdProp
      : Number(dialogueIdParam) || undefined;

  // Use ChatStore - single source of truth for all state
  const {
    dialogue: storeDialogue,
    projectDatasets,
    uploadedFiles,
    addUploadedFile,
    removeUploadedFile,
    updateUploadedFile,
  } = useChatStore();

  const effectiveDialogue = dialogue ?? storeDialogue ?? [];

  // Read-only viewer: a signed-in user who opened someone else's *shared*
  // dialogue. The backend already rejects their writes (send/start are
  // owner-gated), so hiding the composer is defense-in-depth UX, not the
  // enforcement boundary. Reads from the cached dialogue query (populated by
  // useLoadDialogue) so it adds no extra request, and is never read-only on the
  // splash screen where there is no dialogueId.
  const currentUser = useGetUserDetails();
  const { data: dialogueData } = useGetDialogueById({ dialogueId });
  const isReadOnly =
    !!dialogueId &&
    dialogueData?.user_id != null &&
    (!currentUser || dialogueData.user_id !== currentUser.id);

  // Compute last user message for Up arrow recall
  const lastUserMessage = useMemo(() => {
    for (let i = effectiveDialogue.length - 1; i >= 0; i--) {
      if (effectiveDialogue[i].type === "User") {
        const textPart = effectiveDialogue[i].contentParts?.find(
          (p) => p.type === ContentType.TEXT,
        );
        return textPart?.text || "";
      }
    }
    return "";
  }, [effectiveDialogue]);

  // Pop the last staged message back into the input for editing (Up arrow)
  const editLastStagedMessage = useCallback(() => {
    if (pendingMessageQueue.length > 0 && removeQueuedMessage) {
      const last = pendingMessageQueue[pendingMessageQueue.length - 1];
      removeQueuedMessage(pendingMessageQueue.length - 1);
      setUserInput(last.text);
    }
  }, [pendingMessageQueue, removeQueuedMessage]);

  // Convert project datasets to file display format ONLY if we're in a project context
  // (i.e., we have a dialogueId and project datasets). This prevents project datasets
  // from appearing on the splash page after navigating from a project.
  const projectFilesAsUploadState: FileUploadState[] =
    dialogueId && projectDatasets.length > 0
      ? projectDatasets
          .filter(
            (dataset: any) => !dataset.name?.match(/^dialogue_\d+_uploads$/i),
          ) // Skip dialogue upload containers
          .flatMap((dataset: any) => {
            // Check if this is a project dataset based on naming pattern
            const isProjectDataset = !!dataset.name?.match(
              /^project_\d+_uploads$/i,
            );

            // If the dataset has individual files, expand them
            if (
              dataset.files &&
              Array.isArray(dataset.files) &&
              dataset.files.length > 0
            ) {
              return dataset.files.map((file: any) => {
                const fileObj = new File([], file.filename);
                const sourceDataRecordId = file.source_data_record_id;
                const datasetId = dataset.id;

                // Store SDR ID on the file object for AttachmentListItem to access
                if (sourceDataRecordId) {
                  (fileObj as any).sourceDataRecordId = sourceDataRecordId;
                }
                if (datasetId) {
                  (fileObj as any).datasetId = datasetId;
                }

                return {
                  file: fileObj,
                  id: `project-${dataset.id}-file-${file.source_data_record_id}`,
                  datasetId: datasetId,
                  sourceDataRecordId: sourceDataRecordId,
                  uploadStatus: "uploaded" as const,
                  uploadProgress: 100,
                  processingStatus:
                    file.status === "completed"
                      ? "completed"
                      : file.is_processing
                        ? "processing"
                        : ("pending" as const),
                  processingProgress: file.progress_percentage || 100,
                  preview: undefined,
                  sizeFormatted: undefined,
                  isFromProject: isProjectDataset,
                };
              });
            } else {
              // Fallback to using dataset name if no individual files
              const fileObj = new File(
                [],
                dataset.display_name || dataset.name,
              );
              const datasetId = dataset.id;
              const sourceDataRecordId = (dataset as any)
                .source_data_records?.[0]?.id;

              // Store SDR ID on the file object for AttachmentListItem to access
              if (sourceDataRecordId) {
                (fileObj as any).sourceDataRecordId = sourceDataRecordId;
              }
              if (datasetId) {
                (fileObj as any).datasetId = datasetId;
              }

              return [
                {
                  file: fileObj,
                  id: `project-${dataset.id}`,
                  datasetId: datasetId,
                  sourceDataRecordId: sourceDataRecordId,
                  uploadStatus: "uploaded" as const,
                  uploadProgress: 100,
                  processingStatus: "pending" as const,
                  processingProgress: 0,
                  preview: undefined,
                  sizeFormatted: undefined,
                  isFromProject: isProjectDataset,
                },
              ];
            }
          })
      : [];

  // Handle file changes - update store directly, no prop drilling
  const handleFilesChange = (files: FileUploadState[]) => {
    // Update store with new uploaded files (excluding project-specific display files, not project attachments)
    // We want to keep proj-attachment-* files but not project-* display files
    const adhocFiles = files.filter((f) => !f.id.startsWith("project-"));

    // Preserve private dataset documents from dialogue history, project attachments, AND preloaded SDR files
    const preservedFiles = uploadedFiles.filter(
      (file: any) =>
        file.isFromDialogueHistory === true ||
        file.isFromProject === true ||
        file.sourceDataRecordId, // Preloaded from "Chat with Document"
    );

    // Also track files that are still processing (upload in progress)
    const processingFiles = uploadedFiles.filter(
      (file: any) =>
        file.uploadStatus !== "uploaded" ||
        (file.processingStatus !== "completed" &&
          file.processingStatus !== "error"),
    );

    // Guard: If hook reports empty files but we have preserved or processing files, skip clearing.
    // This protects against initialization race conditions where hook initializes before store is populated
    // or when screen transitions cause the hook to re-initialize.
    if (
      files.length === 0 &&
      (preservedFiles.length > 0 || processingFiles.length > 0)
    ) {
      return;
    }

    // Get IDs of files from the hook (excluding project display files)
    const incomingFileIds = new Set(adhocFiles.map((f) => f.id));

    // Get ad-hoc files currently in store (non-preserved)
    const storeAdhocFiles = uploadedFiles.filter(
      (file: any) =>
        !file.isFromDialogueHistory &&
        !file.isFromProject &&
        !file.sourceDataRecordId,
    );

    // Remove files that are no longer in the hook's list
    storeAdhocFiles.forEach((file: any) => {
      if (!incomingFileIds.has(file.id)) {
        removeUploadedFile(file.id);
      }
    });

    // Add or update files from the hook
    adhocFiles.forEach((file) => {
      const existingFile = uploadedFiles.find((f: any) => f.id === file.id);
      if (existingFile) {
        updateUploadedFile(file.id, {
          uploadStatus: file.uploadStatus,
          uploadProgress: file.uploadProgress,
          processingStatus: file.processingStatus,
          processingProgress: file.processingProgress,
          datasetId: file.datasetId,
          sourceDataRecordId: file.sourceDataRecordId,
        });
      } else {
        addUploadedFile(file);
      }
    });
  };

  // Handle file removal - sync with store when hook removes a file
  const handleRemoveFile = (file: FileUploadState) => {
    removeUploadedFile(file.id);
  };

  // Onboarding session: a simple Q&A loop with no agent-side tool cycles.
  // Staging messages for "next handoff point" makes no sense here — the
  // message should appear as a regular user message and the backend
  // advances immediately. Derived from persona slug (source of truth)
  // rather than a separate store flag so it survives page reloads.
  const isOnboardingSession = useChatStore(
    (s) => s.persona?.slug === "ftux-onboarding",
  );

  // handleFormSubmit receives images directly from ChatPromptInput - no sync needed
  // Note: modelId can be null - backend will default to GeneralLLM
  const handleFormSubmit = async (images?: ImageContent[]) => {
    if (
      !isOnboardingSession &&
      shouldStageMessageForTurnHandoff({
        isChatLoading,
        isTurnActive,
      })
    ) {
      // Agent is running — queue locally, will be sent when turn ends
      if (userInput.trim() === "" || !queueMessage) return;
      const queuedSendId = newSendId();
      trackEvent("ai_message_sent", {
        dialogue_id: dialogueId,
        send_id: queuedSendId,
        target: "queue",
        has_attachments: (images?.length ?? 0) > 0,
        attachment_count: images?.length ?? 0,
        message_length: userInput.length,
        is_onboarding: isOnboardingSession,
      });
      queueMessage(userInput, images, { sendId: queuedSendId });
      setUserInput("");
      return;
    }

    // Don't allow empty messages unless there are files or images attached
    const hasImages = images && images.length > 0;
    if (userInput.trim() === "" && allFiles.length === 0 && !hasImages) {
      NucToast({
        message: "Please enter a message or attach files",
        type: "error",
        hideProgressBar: false,
      });
      return;
    }

    const sendId = newSendId();
    trackEvent("ai_message_sent", {
      dialogue_id: dialogueId,
      send_id: sendId,
      target: "chat",
      has_attachments: allFiles.length > 0 || !!hasImages,
      attachment_count: allFiles.length + (images?.length ?? 0),
      message_length: userInput.length,
      is_onboarding: isOnboardingSession,
    });
    const sentSuccessfully = await sendUserMessage(userInput, images, {
      sendId,
      target: "chat",
    });
    if (sentSuccessfully) {
      // Only clear the text input, NOT the files
      // Files persist in store and stay attached for subsequent messages
      setUserInput("");
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUserInput(event.target.value);
  };

  const handleStopChat = () => {
    terminateConnection();
  };

  // Combine project files with ad-hoc uploads from store for display
  // On splash screen: show uploadedFiles (ad-hoc uploads + preloaded SDR files) but NOT project files
  // On dialogue page: show everything (project files + uploaded files)
  const allFiles = isSplashScreen
    ? uploadedFiles
    : [...projectFilesAsUploadState, ...uploadedFiles];

  const currentModule = useOnboardingStore((s) => s.currentModule);
  const isUploadModule =
    isOnboardingSession && currentModule === "context_upload";

  if (isReadOnly) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          py: 1.5,
          px: 2,
          color: "text.secondary",
        }}
        data-testid="aa-readonly-shared-notice"
      >
        <VisibilityOutlined fontSize="small" />
        <Typography variant="body2">
          You&apos;re viewing a shared chat (read-only).
        </Typography>
      </Box>
    );
  }

  return (
    <ChatPromptInput
      onSubmit={handleFormSubmit}
      isInputting={!isTurnActive && userInput !== ""}
      isChatLoading={isChatLoading}
      isGenerating={isTurnActive}
      onStopChat={handleStopChat}
      value={userInput}
      onChange={handleChange}
      onClearInput={() => setUserInput("")}
      lastUserMessage={lastUserMessage}
      onRecallMessage={(text) => setUserInput(text)}
      onFilesChange={handleFilesChange}
      onRemoveFile={handleRemoveFile}
      existingFiles={allFiles}
      InputBar={InputBar}
      InputButtons={InputButtons}
      isSplashScreen={isSplashScreen}
      tokenUsage={tokenUsage}
      placeholder={
        isUploadModule
          ? "Upload a document or type to continue..."
          : (composerPlaceholder ?? "Ask AtomAssist anything...")
      }
      product="atom-assist"
      stagedMessages={pendingMessageQueue}
      onEditLastStagedMessage={editLastStagedMessage}
      onRemoveStagedMessage={removeQueuedMessage}
    />
  );
};

export default AtomAssistChatInput;
